import { Grid } from "react-virtualized";
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  selectIsTranslatedEnvironmentsInitialized,
  selectTranslatedEnvironments,
  selectTranslatedEnvironmentsSearch,
} from "../../app/slices/EnvironmentSlice";
import { RenderedSection } from "react-virtualized/dist/es/Grid";
import { TranslatedEnvironmentDTO } from "../../dto/TranslatedEnvironmentDTO";
import useTranslatedEnvironments from "./TranslatedEnvironments.hook";
import TranslatedEnvironmentCard from "./TranslatedEnvironmentCard";

/**
 * Virtualized grid of environment cards. Fills its parent; pages are fetched
 * lazily as rows scroll into view (see useTranslatedEnvironments.onRerender).
 * `search` filters by name on the server.
 */
export default function TranslatedEnvironmentView(props: {
  selectedEnvironmentId?: string;
  onSelectEnvironment: (environment: TranslatedEnvironmentDTO) => void;
  search?: string;
  ariaLabel?: string;
}) {
  const minColumnWidth = 260;
  const rowHeight = 236;
  const scrollbarGutter = 12;
  const { selectedEnvironmentId, onSelectEnvironment, ariaLabel } = {
    ...props,
  };
  const search = props.search ?? "";
  const { t } = useTranslation();
  const translatedEnvironments = useSelector(selectTranslatedEnvironments);
  const isInitialized = useSelector(selectIsTranslatedEnvironmentsInitialized);
  const cachedSearch = useSelector(selectTranslatedEnvironmentsSearch);
  const parentRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const { onRerender } = useTranslatedEnvironments(search);

  const showNoMatches =
    isInitialized &&
    search !== "" &&
    cachedSearch === search &&
    translatedEnvironments.length === 0;

  useEffect(() => {
    const measure = () => {
      if (!parentRef.current) return;
      setSize({
        width: parentRef.current.clientWidth,
        height: parentRef.current.clientHeight,
      });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const amountOfColumns = Math.max(1, Math.floor((size.width - scrollbarGutter) / minColumnWidth));
  const rowCount = Math.ceil(translatedEnvironments.length / amountOfColumns);

  const cellRenderer = ({
    columnIndex,
    key,
    rowIndex,
    style,
  }: {
    columnIndex: number;
    key: string;
    rowIndex: number;
    style: CSSProperties;
  }) => {
    const index = amountOfColumns * rowIndex + columnIndex;
    if (index >= translatedEnvironments.length) return <div key={key}></div>;
    const environment = translatedEnvironments[index];
    return (
      <TranslatedEnvironmentCard
        environment={environment}
        key={key}
        style={style}
        selected={
          !!selectedEnvironmentId && environment?.id === selectedEnvironmentId
        }
        onSelectEnvironment={onSelectEnvironment}
      />
    );
  };

  function onSectionRendered({ rowStartIndex, rowStopIndex }: RenderedSection) {
    onRerender(amountOfColumns, rowStartIndex, rowStopIndex);
  }

  return (
    <div
      ref={parentRef}
      className={"w-full h-full relative overflow-hidden"}
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid="translated-environment-view"
    >
      {showNoMatches && (
        <div
          className="absolute inset-0 flex items-center justify-center px-[24px] text-center text-[14px] text-creator-text-sub"
          data-testid="translated-environment-view-no-matches"
        >
          {t("new_training_wizard.no_environments_found", { search })}
        </div>
      )}
      {translatedEnvironments.length > 0 && size.width > 0 && (
        <Grid
          // remount per search so a new result list starts at the top
          key={cachedSearch}
          cellRenderer={cellRenderer}
          columnCount={amountOfColumns}
          // Leave room for the vertical scrollbar so the last column is not clipped.
          columnWidth={(size.width - scrollbarGutter) / amountOfColumns}
          style={{ overflowX: "hidden" }}
          height={size.height}
          rowCount={rowCount}
          rowHeight={rowHeight}
          width={size.width}
          onSectionRendered={onSectionRendered}
          className={"pit-creator-scrollbar"}
        />
      )}
    </div>
  );
}
